import React, { useEffect, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import UpdateCashierDataApi from '../../../services/cashier/updateCashier';
import { toast } from 'react-toastify';
import CASHIER_TOASTS from '../../../utils/constants/toastMesages/cashierToastMessage';

/**
 * Modal component to edit a cashier.
 *
 * @param {Object} props
 * @param {boolean} props.show - Modal visibility
 * @param {Function} props.onHide - Function to hide modal
 * @param {Object} props.cashier - Cashier object to edit
 *
 * @returns {JSX.Element}
 */
function EditCashierModal({ show, onHide, cashier }) {

  //States for storing form data
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: '',
    email: '',
    active: false
  });

  //States for showing error message
  const [passwordError, setPasswordError] = useState("");

  //UseEffect hook for showng and populating cashier data as soon as it renders
  useEffect(() => {
    if (cashier) {
      setFormData({
        name: cashier.name || '',
        username: cashier.username || '',
        password: '',
        email: cashier.email,
        role: cashier.role || 'cashier',
        active: cashier.active || false
      });
      setPasswordError("");
    }
  }, [cashier]);

  //Handler function for tracking all the changes in input and checkbox fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === "password") {
      if (value.length > 0 && value.length < 6) {
        setPasswordError("Password must be at least 6 characters");
      } else {
        setPasswordError("");
      }
    }
  };

  //Function for form validation
  const validateForm = () => {
    if (
      formData.name.trim() === '' ||
      formData.username.trim() === ''
    ) {
      toast.error("Name and Username are required");
      return false;
    }

    if (formData.password && formData.password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }

    return true;
  };

  //Handler function or saving/uploading cashier data to backend
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (!cashier || !cashier._id) {
        toast.error(CASHIER_TOASTS.INVALID_CASHIER);
        return;
      }

      const usernameWithSuffix = formData.username.endsWith('_orderly')
        ? formData.username
        : `${formData.username}_orderly`;

      const updatedData = {
        ...formData,
        username: usernameWithSuffix
      };

      await UpdateCashierDataApi(cashier._id, updatedData);
      toast.success(CASHIER_TOASTS.CASHIER_UPDATED);
      onHide();
    } catch (error) {
      toast.error(CASHIER_TOASTS.CASHIER_NOT_UPDATED);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Cashier</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="formName" className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onKeyDown={(e) => {if (e.key === "Enter") handleSave()}}
            />
          </Form.Group>
           <Form.Group controlId="formName" className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter name"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onKeyDown={(e) => {if (e.key === "Enter") handleSave()}}
            />
          </Form.Group>

          <Form.Group controlId="formUsername" className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              onKeyDown={(e) => {if (e.key === "Enter") handleSave()}}
            />
            <Form.Text className="text-muted">"_orderly" will be added automatically</Form.Text>
          </Form.Group>

          <Form.Group controlId="formPassword" className="mb-3">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              isInvalid={passwordError !== ""}
              onKeyDown={(e) => {if (e.key === "Enter") handleSave()}}
            />
            {passwordError && (
              <Form.Text className="text-danger">{passwordError}</Form.Text>
            )}
          </Form.Group>

          <Form.Group controlId="formActive" className="mb-3">
            <Form.Check
              type="switch"
              label="Active"
              name="active"
              checked={formData.active}
              onChange={handleChange}
              onKeyDown={(e) => {if (e.key === "Enter") handleSave()}}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditCashierModal;
